import { CircleDashed } from "lucide-react";
import { useMemo } from "react";

import { useActiveStaff, useProjectMembers } from "@/features/projects/api";
import { personOfStaff } from "@/features/projects/lib/members";
import { personOption } from "@/features/projects/lib/pickerOptions";
import type { Task } from "@/features/tasks/lib/board";
import { useT } from "@/shared/i18n/useT";
import type { ComboboxOption } from "@/shared/ui/Combobox";

/** The assignee pickers' value for "nobody". */
export const UNASSIGNED = "unassigned";

/** As the server allows. */
export const MAX_ASSIGNEES = 10;

const NOBODY: Task["assignees"] = [];

/**
 * Who a project's tasks can be assigned to: its members, with their project role, and an
 * "Unassigned" option to put first. `keep` adds current assignees who left the project. A task
 * outside any project (a null key) can go to any active staff member.
 */
export function useAssigneeOptions(
  projectKey: string | null,
  { enabled = true, keep = NOBODY }: { enabled?: boolean; keep?: Task["assignees"] } = {},
) {
  const t = useT();
  const inProject = projectKey !== null;
  const members = useProjectMembers(projectKey ?? "", enabled && inProject);
  const staff = useActiveStaff(enabled && !inProject);

  const unassigned = useMemo<ComboboxOption>(
    () => ({
      value: UNASSIGNED,
      label: t("task.unassigned"),
      leading: <CircleDashed className="size-5 shrink-0 text-ink-faint" aria-hidden="true" />,
    }),
    [t],
  );

  /** Everyone the pickers offer, as tasks name people. */
  const candidates = useMemo<Task["assignees"]>(
    () =>
      inProject
        ? (members.data ?? []).map((member) => member.user)
        : (staff.data ?? []).map(personOfStaff),
    [inProject, members.data, staff.data],
  );

  const people = useMemo<ComboboxOption[]>(() => {
    const roles = new Map((members.data ?? []).map((member) => [member.user.id, member.role]));
    const list = candidates.map((person) => {
      const role = inProject ? roles.get(person.id) : undefined;
      return personOption(person, role ? t(`projectRole.${role}`) : undefined);
    });
    for (const person of keep) {
      if (!list.some((option) => option.value === person.id)) list.push(personOption(person));
    }
    return list;
  }, [candidates, inProject, members.data, keep, t]);

  const isPending = inProject ? members.isPending : staff.isPending;
  return { unassigned, people, candidates, isPending };
}
